/**
 * IhogRingsController — effect-only child component for IHOG proximity rings.
 *
 * Extracted from MapView.tsx as part of the Claude audit 2026-05-18
 * finding 7 part 2 (component split, wave 3). Owns its own narrow zustand
 * subscriptions so its effects only re-run when ihog-ring-related state
 * changes, not on every MapView render.
 *
 * Returns null — no DOM output.
 */
import { useEffect } from 'react';
import type { GeoJSONSource } from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useIncidentStore } from '../../stores/incidentStore';
import { buildIhogRingsFC } from '../ihogRings';
import { getMapInstance, safeGetSource } from '../MapView';
import { registerStyleRebuild } from '../layerVisibilityRegistry';
import React from 'react';

// HI-1 step 3 (2026-05-23): rebuildRef prop removed. The controller now
// registers its style-rebuild restore with layerVisibilityRegistry.
export function IhogRingsController() {
  const showIhogRings = useLayerVisibilityStore((s) => s.layers.ihogRings);
  // (Claude audit 2026-05-18 finding 6) Subscribe to a content-keyed
  // signature of the incident list, not the whole list reference. The
  // ihog-rings effect only depends on lat/lon/status; most incident
  // edits don't touch any of those, yet were rebuilding the FC on every
  // patch.
  const incidentsRingsKey = useIncidentStore((s) =>
    s.list
      .map(
        (i) =>
          `${i.id}:${i.status}:${i.latitude ?? ''}:${i.longitude ?? ''}:${i.ihog_contact_nm ?? ''}:${i.ihog_exclusion_nm ?? ''}`,
      )
      .join('|'),
  );

  // IHOG proximity rings (12 NM contact + 7 NM exclusion) around
  // active incidents in the org's incidents store. Source data is
  // in-memory (incidents store) rather than a fetched overlay, so
  // the effect rebuilds the FeatureCollection whenever the incidents
  // list changes — not on viewport change.
  useEffect(() => {
    function apply() {
      const map = getMapInstance();
      if (!map || !map.getLayer('ihog-rings-fill')) return;
      const v = showIhogRings ? 'visible' : 'none';
      map.setLayoutProperty('ihog-rings-fill',  'visibility', v);
      map.setLayoutProperty('ihog-rings-line',  'visibility', v);
      map.setLayoutProperty('ihog-rings-label', 'visibility', v);
      if (!showIhogRings) return;
      // Read the actual list via getState() inside the effect; only
      // the content key is reactive.
      const fc = buildIhogRingsFC(useIncidentStore.getState().list);
      const src = safeGetSource<GeoJSONSource>(map, 'ihog-rings');
      if (src) src.setData(fc as any);
    }
    // HI-1 step 2/3: style-rebuild restore registered with the central
    // registry.
    const unregister = registerStyleRebuild({
      id: 'ihogRings',
      restore: () => apply(),
    });
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { unregister(); };
  }, [showIhogRings, incidentsRingsKey]);

  return null;
}
